import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { DownloadOutlined, LeftOutlined, SearchOutlined } from "@ant-design/icons";
import { Alert, Input, Select, message } from "antd";
import type { Dayjs } from "dayjs";
import { ChevronDown, ChevronUp, Filter } from "react-feather";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import CustomDatePicker from "../../../CommonComponents/CustomDatePicker/CustomDatePicker";
import AppContext from "../../../../context/AppContext";
import { useDebouncedSearchQuery } from "../../../../hooks/useDebouncedSearchQuery";
import { useSgpRiskBetsList, type SgpRiskBetsListSortBy } from "../../../../queries/sportsbook/sgpRiskBetsList";
import { SIDER_TABS } from "../../../../utils/Constants";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { usePageTitle } from "../../../CommonComponents/Layout/PageTitleContext";

import SGPIdBetsListTable from "./SGPIdBetsListTable";
import {
  SGP_ID_BETS_LIST_STATUS_OPTIONS,
  type SgpIdBetStatusFilter,
} from "./sgpIdBetsListData";
import {
  buildSgpRiskPath,
  parseSgpRiskBetsListSearchParams,
  resolveReportingDatesFromIso,
} from "../sgpRiskNavigation";

import styles from "./SGPIdBetsList.module.css";

interface BetsListFilters {
  minBetAmount: string;
  minWinAmount: string;
  status: SgpIdBetStatusFilter | "";
  minOdds: string;
}

const INITIAL_FILTERS: BetsListFilters = {
  minBetAmount: "",
  minWinAmount: "",
  status: "",
  minOdds: "",
};

const DEFAULT_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

type BetsListSortState = {
  sortBy: SgpRiskBetsListSortBy;
  sortOrder: "asc" | "desc";
};

const DEFAULT_SORT: BetsListSortState = {
  sortBy: "created_at",
  sortOrder: "desc",
};

function parseOptionalNumber(value: string): number | null {
  if (value === "" || value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function SGPIdBetsList() {
  const { setActiveSidebarTab } = useContext(AppContext);
  const navigate = useNavigate();
  const { sgpId } = useParams<{ sgpId?: string }>();
  const [searchParams] = useSearchParams();
  const [messageApi, contextHolder] = message.useMessage();
  const [searchValue, setSearchValue] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<BetsListFilters>(INITIAL_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<BetsListFilters>(INITIAL_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortState, setSortState] = useState<BetsListSortState>(DEFAULT_SORT);
  const routeContext = useMemo(() => parseSgpRiskBetsListSearchParams(searchParams), [searchParams]);
  const [reportingDates, setReportingDates] = useState<[Dayjs, Dayjs]>(() =>
    resolveReportingDatesFromIso(routeContext.startDate, routeContext.endDate),
  );

  const selectedSgpId = useMemo(() => {
    const normalized = sgpId?.trim();
    return normalized || "";
  }, [sgpId]);

  const hasRequiredContext = Boolean(
    selectedSgpId && routeContext.sportId && routeContext.leagueId,
  );

  const displaySgpId = useMemo(
    () => selectedSgpId || "unknown-sgp-id",
    [selectedSgpId],
  );

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS.SPORTSBOOK, "SPORTSBOOK_SGP_RISK");
  }, [setActiveSidebarTab]);

  const handleReportingRangeQuery = useCallback(() => {
    setCurrentPage(1);
  }, []);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
    setCurrentPage(1);
  };

  const { effectiveSearchQuery } = useDebouncedSearchQuery(searchValue, {
    delayMs: SEARCH_DEBOUNCE_MS,
    minLength: 1,
    treatEmptyAsCleared: true,
  });

  const updateFilter = <K extends keyof BetsListFilters>(
    key: K,
    value: BetsListFilters[K],
  ) => {
    setFilters((previous) => ({ ...previous, [key]: value }));
  };

  const isFiltersDirty = useMemo(
    () =>
      Object.keys(INITIAL_FILTERS).some((key) => {
        const filterKey = key as keyof BetsListFilters;
        return filters[filterKey] !== INITIAL_FILTERS[filterKey];
      }),
    [filters],
  );

  const handleClearFilters = () => {
    setFilters(INITIAL_FILTERS);
    setAppliedFilters(INITIAL_FILTERS);
    setCurrentPage(1);
    messageApi.success(AppStrings.FILTERS_CLEARED);
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (nextPageSize: number) => {
    setPageSize(nextPageSize);
    setCurrentPage(1);
  };

  const handleSortChange = useCallback((nextSortState: BetsListSortState) => {
    setSortState(nextSortState);
    setCurrentPage(1);
  }, []);

  const listQueryParams = useMemo(
    () => ({
      sgp_id: selectedSgpId,
      sport_id: routeContext.sportId,
      league_id: routeContext.leagueId,
      start_date: reportingDates?.[0]?.startOf("day")?.toISOString(),
      end_date: reportingDates?.[1]?.endOf("day")?.toISOString(),
      page: currentPage,
      page_size: pageSize,
      search_term: effectiveSearchQuery.trim() || null,
      min_bet_amount: parseOptionalNumber(appliedFilters.minBetAmount),
      min_win_amount: parseOptionalNumber(appliedFilters.minWinAmount),
      min_odds: parseOptionalNumber(appliedFilters.minOdds),
      status: appliedFilters.status || null,
      sort_by: sortState.sortBy,
      sort_order: sortState.sortOrder,
    }),
    [
      appliedFilters,
      currentPage,
      effectiveSearchQuery,
      pageSize,
      reportingDates,
      routeContext.leagueId,
      routeContext.sportId,
      selectedSgpId,
      sortState,
    ],
  );

  const listQuery = useSgpRiskBetsList(listQueryParams);
  const isPaginating = listQuery.isFetching && listQuery.isPlaceholderData;
  const isTableLoading = listQuery.isLoading || isPaginating;
  const shouldHoldTableRows = listQuery.isLoading;
  const tableRows = useMemo(
    () => (shouldHoldTableRows ? [] : (listQuery.data?.rows ?? [])),
    [shouldHoldTableRows, listQuery.data?.rows],
  );
  const tableTotalRows = shouldHoldTableRows
    ? 0
    : (listQuery.data?.pagination?.total_results ?? 0);

  const displaySportName = useMemo(() => {
    const fromRoute = routeContext.sportName?.trim();
    if (fromRoute) {
      return fromRoute;
    }

    const fromRow = tableRows[0]?.sport?.trim();
    if (fromRow) {
      return fromRow;
    }

    return routeContext.sportId?.trim() || "";
  }, [routeContext.sportId, routeContext.sportName, tableRows]);

  const displayLeagueName = useMemo(() => {
    const fromRoute = routeContext.leagueName?.trim();
    if (fromRoute) {
      return fromRoute;
    }

    const fromRow = tableRows[0]?.league?.trim();
    if (fromRow) {
      return fromRow;
    }

    return routeContext.leagueId?.trim() || "";
  }, [routeContext.leagueId, routeContext.leagueName, tableRows]);

  const contextSubtitleParts = useMemo(
    () =>
      [
        { key: "sgp-id", value: displaySgpId, isPrimary: true },
        displaySportName
          ? { key: "sport", value: displaySportName, isPrimary: false }
          : null,
        displayLeagueName
          ? { key: "league", value: displayLeagueName, isPrimary: false }
          : null,
      ].filter(Boolean) as Array<{
        key: string;
        value: string;
        isPrimary: boolean;
      }>,
    [displayLeagueName, displaySgpId, displaySportName],
  );

  const pageTitle = useMemo(
    () =>
      `SGP ID Bets List (${contextSubtitleParts
        .map((part) => part.value)
        .join(" · ")})`,
    [contextSubtitleParts],
  );
  usePageTitle(pageTitle);

  return (
    <>
      <div className={styles.page} data-testid="sgp-id-bets-list">
        {contextHolder}
        <div className={styles.header}>
          <div className={styles.headerMain}>
            <div className={styles.backRow}>
              <button
                type="button"
                className={styles.backButton}
                aria-label="Back To SGP Risk"
                onClick={() =>
                  navigate(
                    buildSgpRiskPath({
                      startDate: reportingDates[0].startOf("day").toISOString(),
                      endDate: reportingDates[1].endOf("day").toISOString(),
                    }),
                  )
                }
              >
                <LeftOutlined className={styles.backButtonIcon} />
              </button>
            </div>
          </div>
          <div className={styles.headerActions}>
            <div
              className={styles.datePicker}
              data-testid="sgp-id-bets-list-date-picker"
            >
              <CustomDatePicker
                dates={reportingDates}
                setDates={setReportingDates}
                getData={handleReportingRangeQuery}
                showPresets
              />
            </div>
            <button
              type="button"
              className={styles.downloadButton}
              aria-label="Download"
              title="CSV download is unavailable until the export API is available."
              disabled
            >
              <DownloadOutlined />
            </button>
          </div>
        </div>

        {!hasRequiredContext && (
          <Alert
            type="warning"
            showIcon
            message="Missing SGP context."
            description="Open this page from the SGP Risk table so sport and league identifiers are included."
            style={{ marginBottom: 16 }}
          />
        )}

        {listQuery.isError && (
          <Alert
            type="error"
            showIcon
            message="Unable to load SGP bets."
            style={{ marginBottom: 16 }}
          />
        )}

        <div className={styles.searchBar}>
          <div className={styles.searchWrapper}>
            <Input
              placeholder="Search"
              prefix={
                <SearchOutlined
                  style={{ fontSize: "16px", color: "var(--grey-400)" }}
                />
              }
              value={searchValue}
              onChange={handleSearchChange}
              className={styles.searchInput}
              allowClear
            />
          </div>

          <div className={styles.searchDivider} />

          <button
            type="button"
            className={styles.filterButton}
            data-testid="sgp-id-bets-list-filter-toggle"
            aria-label="Toggle filters"
            aria-expanded={showFilters}
            onClick={() => setShowFilters((current) => !current)}
          >
            <Filter className={styles.filterIcon} size={18} />
            {showFilters ? (
              <ChevronUp className={styles.filterIcon} size={16} />
            ) : (
              <ChevronDown className={styles.filterIcon} size={16} />
            )}
          </button>
        </div>

        {showFilters && (
          <div className={styles.filtersPanel} data-testid="sgp-id-bets-list-filters-panel">
            <div className={styles.filtersGrid}>
              <div className={styles.filterField}>
                <label
                  className={styles.filterLabel}
                  htmlFor="sgp-id-bets-list-min-bet-amount"
                >
                  Min Bet Amount
                </label>
                <Input
                  id="sgp-id-bets-list-min-bet-amount"
                  placeholder="Text"
                  value={filters.minBetAmount}
                  onChange={(event) =>
                    updateFilter("minBetAmount", event.target.value)
                  }
                  className={styles.filterInput}
                />
              </div>

              <div className={styles.filterField}>
                <label
                  className={styles.filterLabel}
                  htmlFor="sgp-id-bets-list-min-win-amount"
                >
                  Min Win Amount
                </label>
                <Input
                  id="sgp-id-bets-list-min-win-amount"
                  placeholder="Text"
                  value={filters.minWinAmount}
                  onChange={(event) =>
                    updateFilter("minWinAmount", event.target.value)
                  }
                  className={styles.filterInput}
                />
              </div>

              <div className={styles.filterField}>
                <label
                  className={styles.filterLabel}
                  htmlFor="sgp-id-bets-list-status"
                >
                  Status
                </label>
                <Select
                  id="sgp-id-bets-list-status"
                  value={filters.status || undefined}
                  placeholder={AppStrings.NOT_SELECTED}
                  onChange={(value) => updateFilter("status", value ?? "")}
                  options={SGP_ID_BETS_LIST_STATUS_OPTIONS}
                  className={styles.filterSelect}
                  allowClear
                />
              </div>

              <div className={styles.filterField}>
                <label
                  className={styles.filterLabel}
                  htmlFor="sgp-id-bets-list-min-odds"
                >
                  Min Odds
                </label>
                <Input
                  id="sgp-id-bets-list-min-odds"
                  placeholder="Text"
                  value={filters.minOdds}
                  onChange={(event) => updateFilter("minOdds", event.target.value)}
                  className={styles.filterInput}
                />
              </div>
            </div>

            <div className={styles.filterActions}>
              <button
                type="button"
                className={`${styles.filterAction} ${styles.filterActionClear}`}
                data-testid="sgp-id-bets-list-clear-filters"
                onClick={handleClearFilters}
                disabled={!isFiltersDirty}
              >
                {AppStrings.CLEAR_FILTERS}
              </button>
              <button
                type="button"
                className={`${styles.filterAction} ${styles.filterActionApply}`}
                data-testid="sgp-id-bets-list-apply-filters"
                onClick={handleApplyFilters}
              >
                {AppStrings.APPLY}
              </button>
            </div>
          </div>
        )}

        <SGPIdBetsListTable
          rows={tableRows}
          totalRows={tableTotalRows}
          loading={isTableLoading}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={handlePageSizeChange}
          sortBy={sortState.sortBy}
          sortOrder={sortState.sortOrder}
          onSortChange={handleSortChange}
        />
      </div>
    </>
  );
}

export default SGPIdBetsList;
